import type { ApolloClient } from '@apollo/client';
import type { NotificationStoreActionsType } from '~/components/NotificationTray/NotificationStore';
import type {
  ActionAddFiles,
  ActionRemoveFile,
  ActionSetError,
  ActionSetFileDone,
  ActionSetIsUploadStarted,
  ActionSetProgress,
  QueueReducerAction,
} from './queueReducer';
import { queueReducer, types as queueTypes } from './queueReducer';
import type {
  AdditionalFileStateMetadata,
  ApplicationState,
  FileState,
  QueueState,
  UploaderFunctions,
} from './types';
import { defaultOnFileUploaded } from './prebuiltEntityCreators';
import { GLOBAL_CALLBACKS_IDENTIFIER } from './getCallbacks';

export const initialState = {
  uploadQueue: [],
  isUploadStarted: false,
  isUploadFinished: false,
  mediaCount: null,
  isUploading: false,
  haveAnyUploadsFailed: false,
  limitExceededMessage: '',
  uploadMetadata: {},
  numberOfFilesInUploadAttempt: 0,
  onFileUploaded: defaultOnFileUploaded,
};

const types = {
  register: 'register' as const,
  setMediaCount: 'set media count' as const,
  setNumberOfFilesInUploadAttempt: 'set number of files in upload attempt' as const,
  setMediaLimitExceededMessage: 'set media limit exceeded message' as const,
  setUploadMetadata: 'set additional data to pass to the granary' as const,
  setOnFileUploaded: 'set on file uploaded callback' as const,
  ...queueTypes,
};

interface ActionRegister {
  type: 'register';
  containerId: string;
}

interface ActionSetOnFileUploaded {
  type: 'set on file uploaded callback';
  payload: {
    onFileUploaded:
      | ((
          _: ApolloClient<object>,
          { uploadedFileUrl }: { uploadedFileUrl: string },
        ) => { status: string; attributes: { assetUrl: string } })
      | (() => void);
  };
  containerId: string;
}

interface ActionSetUploadMetadata {
  type: 'set additional data to pass to the granary';
  payload: {
    uploadMetadata: Record<string, unknown>;
  };
  containerId: string;
}

interface ActionSetMediaCount {
  type: 'set media count';
  payload: {
    mediaCount: number;
  };
  containerId: string;
}

interface ActionSetNumberOfFilesInUploadAttempt {
  type: 'set number of files in upload attempt';
  payload: {
    numberOfFilesInUploadAttempt: number;
  };
  containerId: string;
}

interface ActionSetMediaLimitExceededMessage {
  type: 'set media limit exceeded message';
  payload: {
    mediaLimitExceededMessage: string;
  };
  containerId: string;
}

export type UploaderReducerAction =
  | ActionRegister
  | ActionSetMediaCount
  | ActionSetMediaLimitExceededMessage
  | ActionSetNumberOfFilesInUploadAttempt
  | ActionSetOnFileUploaded
  | ActionSetUploadMetadata
  | (QueueReducerAction & { containerId: string });

export const actions = {
  register: (
    containerId: string,
  ): {
    type: string;
    containerId: string;
  } => ({
    type: types.register,
    containerId,
  }),

  setOnFileUploaded: (
    containerId: string,
    onFileUploaded:
      | ((
          _: ApolloClient<object>,
          { uploadedFileUrl }: { uploadedFileUrl: string },
        ) => { status: string; attributes: { assetUrl: string } })
      | (() => void),
  ): ActionSetOnFileUploaded => ({
    type: types.setOnFileUploaded,
    payload: { onFileUploaded },
    containerId,
  }),

  setUploadMetadata: (
    containerId: string,
    uploadMetadata: Record<string, unknown>,
  ): ActionSetUploadMetadata => ({
    type: types.setUploadMetadata,
    payload: { uploadMetadata },
    containerId,
  }),

  setFileDone: (
    containerId: string,
    fileId: string,
    payload: ActionSetFileDone['payload']['attributes'],
    completeNotificationAction: NotificationStoreActionsType['setTaskComplete'],
  ): ActionSetFileDone & { containerId: string } => ({
    type: queueTypes.setFileDone,
    payload: { id: fileId, attributes: payload, completeNotificationAction },
    containerId,
  }),

  setNumberOfFilesInUploadAttempt: (
    containerId: string,
    numberOfFiles: number,
  ): ActionSetNumberOfFilesInUploadAttempt => {
    return {
      type: types.setNumberOfFilesInUploadAttempt,
      payload: { numberOfFilesInUploadAttempt: numberOfFiles },
      containerId,
    };
  },

  addFiles: (
    containerId: string,
    files: File[],
    addNotificationAction: NotificationStoreActionsType['addTaskToQueue'],
    cancelUpload: UploaderFunctions['cancelUpload'],
    { mediaGroupId, listId, indexInExistingList, onFileUploaded }: AdditionalFileStateMetadata = {},
  ): ActionAddFiles & { containerId: string } => ({
    type: types.addFiles,
    payload: {
      files,
      mediaGroupId,
      listId,
      indexInExistingList,
      onFileUploaded,
      addNotificationAction,
      cancelUpload,
    },
    containerId,
  }),

  setProgress: (
    containerId: string,
    fileId: string,
    {
      percentageDone,
      speed,
      timeRemaining,
    }: {
      percentageDone: number;
      speed: number;
      timeRemaining: number;
    },
    updateNotificationAction: NotificationStoreActionsType['updateTaskInQueue'],
  ): ActionSetProgress & { containerId: string } => ({
    type: types.setProgress,
    payload: {
      id: fileId,
      percentageDone,
      speed,
      timeRemaining,
      updateNotificationAction,
    },
    containerId,
  }),

  setError: (
    containerId: string,
    fileId: string,
    error: Error,
    failNotificationAction: NotificationStoreActionsType['setTaskFailed'],
  ): ActionSetError & { containerId: string } => ({
    type: types.setError,
    payload: {
      id: fileId,
      error,
      failNotificationAction,
    },
    containerId,
  }),

  removeFile: (
    containerId: string,
    fileId: string,
    removeNotificationAction: NotificationStoreActionsType['removeTaskFromQueue'],
  ): ActionRemoveFile & { containerId: string } => ({
    type: types.removeFile,
    payload: { id: fileId, removeNotificationAction },
    containerId,
  }),

  setIsUploadStarted: (
    containerId: string,
    isUploadStarted: boolean,
  ): ActionSetIsUploadStarted & { containerId: string } => ({
    type: types.setIsUploadStarted,
    payload: { isUploadStarted },
    containerId,
  }),

  setMediaCount: (containerId: string, mediaCount: number): ActionSetMediaCount => ({
    type: types.setMediaCount,
    payload: { mediaCount },
    containerId,
  }),

  setMediaLimitExceededMessage: (
    mediaLimitExceededMessage: string,
  ): ActionSetMediaLimitExceededMessage => ({
    type: types.setMediaLimitExceededMessage,
    payload: { mediaLimitExceededMessage },
    containerId: GLOBAL_CALLBACKS_IDENTIFIER,
  }),
};

const setMediaLimitExceededMessage = (
  state: QueueState,
  action: ActionSetMediaLimitExceededMessage,
) => ({
  ...state,
  mediaLimitExceededMessage: action.payload.mediaLimitExceededMessage,
});

const setIsUploadStarted = (state: QueueState, action: ActionSetIsUploadStarted) => {
  const uploadQueue = queueReducer(state.uploadQueue, action);
  const isUploadFinished = uploadQueue.every(({ isDone }) => isDone) && Boolean(uploadQueue.length);
  return {
    ...state,
    isUploadStarted: action.payload.isUploadStarted,
    isUploading: action.payload.isUploadStarted && !isUploadFinished,
    uploadQueue,
  };
};

const setOnFileUploaded = (state: QueueState, action: ActionSetOnFileUploaded) => ({
  ...state,
  onFileUploaded: action.payload.onFileUploaded,
});

const setUploadMetadata = (state: QueueState, action: ActionSetUploadMetadata) => ({
  ...state,
  uploadMetadata: action.payload.uploadMetadata,
});

const setMediaCount = (state: QueueState, action: ActionSetMediaCount) => ({
  ...state,
  mediaCount: action.payload.mediaCount,
});

const setNumberOfFilesInUploadAttempt = (
  state: QueueState,
  action: ActionSetNumberOfFilesInUploadAttempt,
) => {
  return {
    ...state,
    numberOfFilesInUploadAttempt: action.payload.numberOfFilesInUploadAttempt,
  };
};

const removeDuplicates = (array: FileState[], key: string) => {
  const lookup = new Set();
  return array.filter((obj) => {
    if (lookup.has(obj[key])) {
      return false;
    }
    lookup.add(obj[key]);
    return true;
  });
};

// For single file uploads done one after the other-- i.e., you drop one file in one place in the list,
// then another file in another place in the list -- originally the first
// file would get canceled when the next file was added in.

// To solve that, we check to see if state.uploadQueue and nextUploadQueue have different files
// in them, and if they do, we combine the two queues into one.
const subsequentSingleFileUploads = (
  originalUploadQueue: FileState[],
  subsequentUploadQueue: FileState[],
) =>
  originalUploadQueue.length > 0 &&
  subsequentUploadQueue.length > 0 &&
  originalUploadQueue[0]?.id !== subsequentUploadQueue[0]?.id;

const shouldRemoveFile = (action: UploaderReducerAction) => action.type === 'removeFile';

const updateQueue = (state: QueueState, action: QueueReducerAction | UploaderReducerAction) => {
  const nextUploadQueue: FileState[] = queueReducer(
    state.uploadQueue,
    action as QueueReducerAction,
  );
  let updatedQueue = nextUploadQueue;
  if (subsequentSingleFileUploads(state.uploadQueue, nextUploadQueue)) {
    updatedQueue = [...state.uploadQueue, ...nextUploadQueue];
  }
  if (shouldRemoveFile(action as UploaderReducerAction)) {
    // Sometimes, attempting to cancel multiple files in upload progress can cause duplication of those files.
    updatedQueue = removeDuplicates(nextUploadQueue, 'id');
  }

  const haveAnyUploadsFailed = nextUploadQueue.some(({ error }) => error);
  const isUploadFinished =
    nextUploadQueue.every(({ isDone }) => isDone) && Boolean(nextUploadQueue.length);

  const isUploadStarted = state.isUploadStarted && Boolean(nextUploadQueue.length);
  const isUploading = isUploadStarted && !isUploadFinished;

  return {
    ...state,
    uploadQueue: updatedQueue,
    isUploadFinished,
    haveAnyUploadsFailed,
    isUploading,
    isUploadStarted,
  };
};

const containerReducer = (state: QueueState, action: UploaderReducerAction) => {
  // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
  switch (action.type) {
    case types.setIsUploadStarted:
      return setIsUploadStarted(state, action);
    case types.setMediaCount:
      return setMediaCount(state, action);
    case types.setMediaLimitExceededMessage:
      return setMediaLimitExceededMessage(state, action);
    case types.setOnFileUploaded:
      return setOnFileUploaded(state, action);
    case types.setUploadMetadata:
      return setUploadMetadata(state, action);
    case types.setNumberOfFilesInUploadAttempt:
      return setNumberOfFilesInUploadAttempt(state, action);
    case types.addFiles:
    case types.setProgress:
    case types.setFileDone:
    case types.setError:
    case types.removeFile:
      return updateQueue(state, action);
    default:
      throw new Error('unhandled action');
  }
};

export const reducer = (
  state: ApplicationState,
  action: UploaderReducerAction,
): ApplicationState => {
  if (!action.containerId) {
    throw new Error(`All actions must provide a containerId key, ${action.type}`);
  }

  if (action.type === types.register) {
    return {
      ...state,
      [action.containerId]: initialState,
    };
  }

  const queueState = state[action.containerId];

  return {
    ...state,
    [action.containerId]: containerReducer(queueState, action),
  };
};
